/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable no-console */
/**
 * This file contains functions for interacting with an AI service to get insights from various data structures.
 */

import Deque from '../classes/deque';
import { ParametersType } from '../constants/telosair-interfaces';

// ---------------------------------------------------------------------------------
// Interfaces and Configuration
// ---------------------------------------------------------------------------------

/**
 * Interface for environmental data keyed by group name.
 */
export interface GroupEnvironmentalData {
  [groupName: string]: {
    device: string;
    co2?: number;
    pm25?: number;
    temperature?: number;
    humidity?: number;
    voc?: number;
  };
}

/**
 * Interface for time-series environmental data.
 */
export type TimeseriesData = Record<ParametersType, Deque>;

const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY;
const OPENAI_API_URL = 'https://api.openai.com/v1/responses';

// ---------------------------------------------------------------------------------
// Core AI Service Logic (Generic and Reusable)
// ---------------------------------------------------------------------------------

/**
 * Sends a request to the OpenAI API with a given input string.
 * This is a generic function that can be used by different insight generators.
 * @param input - The combined system prompt and user data string.
 * @returns A promise that resolves to the AI-generated text response.
 */
async function getAIResponse(input: string): Promise<string> {
  if (!OPENAI_API_KEY) {
    throw new Error('OpenAI API key is not configured in environment variables.');
  }

  try {
    const response = await fetch(OPENAI_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${OPENAI_API_KEY}`
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        input
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData?.error?.message || JSON.stringify(errorData);
      throw new Error(`API request failed: ${errorMessage}`);
    }

    const result = await response.json();
    if (
      result &&
      result.output &&
      result.output.length > 0 &&
      result.output[0].content &&
      result.output[0].content.length > 0
    ) {
      return result.output[0].content[0].text.trim();
    }

    throw new Error('Unexpected API response format.');
  } catch (error) {
    console.error('Error fetching AI insights:', error);
    throw error;
  }
}

// ---------------------------------------------------------------------------------
// Prompt Generators
// ---------------------------------------------------------------------------------

/**
 * Generates the system prompt for analyzing group-based environmental data.
 * @returns The system prompt string.
 */
function getGroupSystemPrompt(): string {
  return `
You are an expert environmental data analyst for a system named TelosAir. Your task is to analyze the provided JSON data containing real-time environmental readings from various locations or groups.
**Input Data Format:** JSON object where each key is a 'groupName' (e.g., "Conference Room A").
**Your Goal:** For each group, generate a concise, two-line insight focusing on abnormal values.
- **Line 1:** Summarize the most significant finding.
- **Line 2:** Provide a brief, actionable recommendation.
**Example Insight:**
For Living Room:
High CO2 levels (1450 ppm) suggest inadequate air circulation.
Consider increasing fresh air intake or checking the HVAC filter.
`.trim();
}

/**
 * Generates the system prompt for analyzing time-series environmental data.
 * @returns The system prompt string.
 */
function getTimeseriesSystemPrompt(): string {
  return `
You are an expert environmental data analyst for TelosAir. Your task is to analyze time-series environmental data and generate insights about the different parameters.
Your goal is to summarize the most important trends, patterns, or anomalies across all parameters, focusing on key observations or correlations. If possible, include calculations such as emission rate from spikes in concentration and decay rate for PM2.5 and CO2.
Response should be in markdown format with just three bullet points with a very small brief summary and nothing above and nothing below it.
`.trim();
}

// ---------------------------------------------------------------------------------
// Exported Insight Functions
// ---------------------------------------------------------------------------------

/**
 * Generates insights from group-based environmental data.
 * @param data - The environmental data organized by group name.
 * @returns A promise that resolves to the AI-generated insights.
 */
export async function getGroupInsights(data: GroupEnvironmentalData): Promise<string> {
  const systemPrompt = getGroupSystemPrompt();
  const userData = JSON.stringify(data, null, 2);
  const combinedInput = `${systemPrompt}\n\nAnalyze the following data and provide insights for each group:\n\n${userData}`;
  return getAIResponse(combinedInput);
}

/**
 * Generates insights from time-series environmental data.
 * @param data - The time-series data.
 * @returns A promise that resolves to the AI-generated insights.
 */
export async function getTimeseriesInsights(data: TimeseriesData): Promise<string> {
  const systemPrompt = getTimeseriesSystemPrompt();
  const userData = JSON.stringify(data, null, 2);
  const combinedInput = `${systemPrompt}\n\nAnalyze the following time-series data and provide a summary:\n\n${userData}`;
  return getAIResponse(combinedInput);
}
